<template>
  <div class="position-relative" data-testid="eg-search-select">
    <eg-b-overlay :show="disabled" spinner-small rounding="sm" no-wrap>
      <template #overlay>
        <div />
      </template>
    </eg-b-overlay>
    <eg-dropdown
      ref="dropdown"
      split
      :dropup="dropup"
      class="search-select"
      :menu-class="menuClass"
    >
      <template #button-content="{ toggle }">
        <div
          class="button-content d-flex align-items-center justify-content-between"
          :class="{ 'is-invalid': submitted && !isValid }"
          data-testid="button-content"
          @click.stop="clickValue(toggle)"
        >
          <eg-b-form-input
            v-if="!value?.id || showSearch"
            ref="searchInput"
            :value="searchBoxValue"
            class="search-input"
            :placeholder="placeholder || `${capitalize(entity)} Name`"
            size="sm"
            :state="submitted ? isValid : null"
            data-testid="search-input"
            @input="updateSearch"
            @blur="showSearch = false"
          ></eg-b-form-input>
          <div v-else class="selected-item" data-testid="selected-item">
            <slot v-if="$scopedSlots.item" name="item" :item="value" />
            <div v-else class="text-truncate">
              {{ value && getTitle(value) }}
            </div>
          </div>
          <eg-b-icon
            class="ml-2 fs-10"
            icon="chevron-down"
            data-testid="icon"
          ></eg-b-icon>
        </div>
      </template>
      <template #default>
        <!-- Custom input item shown at the top when typing -->
        <eg-b-dropdown-item-button
          v-if="allowCustomInput && search && search.trim() && !hasExactMatch"
          class="dropdown-item-wrapper custom-input-item"
          data-testid="custom-input-item"
          @click="selectCustomInput"
        >
          <div class="text-truncate">{{ search.trim() }}</div>
        </eg-b-dropdown-item-button>

        <eg-b-dropdown-item-button
          v-if="allowDeselect && !noItemsAvailable"
          class="dropdown-item-wrapper"
          data-testid="deselect-button"
          @click="deselect"
        >
          <div>Skip selecting {{ entity }}</div>
        </eg-b-dropdown-item-button>
        <component
          :is="
            groupFunction && group.items.length ? 'eg-b-dropdown-group' : 'div'
          "
          v-for="(group, index) in filteredItemGroups"
          :key="group.id || index"
          :header="group.title"
          data-testid="item-group"
        >
          <eg-b-dropdown-item-button
            v-for="item in group.items"
            :key="item.id"
            class="dropdown-item-wrapper"
            :active="value && item.id === value.id"
            data-testid="dropdown-item"
            @click="selectItem(item)"
          >
            <slot v-if="$scopedSlots.item" name="item" :item="item" />
            <div v-else class="text-truncate">
              {{ item && getTitle(item) }}
            </div>
          </eg-b-dropdown-item-button>
        </component>
        <div v-if="noItemsAvailable" class="no-items" data-testid="no-items">
          <slot v-if="$scopedSlots['no-items']" name="no-items" />
          <div v-else>{{ emptyTitle || `No ${entityPluralized} created` }}</div>
        </div>
        <div
          v-if="
            search &&
            filteredItemCount === 0 &&
            !(allowCustomInput && search.trim())
          "
          class="no-items"
          data-testid="no-items-found"
        >
          <slot v-if="$scopedSlots['no-items-found']" name="no-items-found" />
          <div v-else>No {{ entityPluralized }} found</div>
        </div>
        <eg-b-dropdown-divider
          v-if="showCreateEntity && !hideCreateDivider"
          class="dropdown-item-wrapper sticky bottom-32"
          data-testid="create-divider"
        />
        <eg-b-dropdown-item-button
          v-if="showCreateEntity"
          class="dropdown-item-wrapper sticky bottom-0 create-item"
          data-testid="create-button"
          @click="createItem"
        >
          {{ `Create ${entity}` }}
        </eg-b-dropdown-item-button>
      </template>
    </eg-dropdown>
  </div>
</template>

<script>
import EgDropdown from "@/components/global/eg_dropdown";
import { capitalize } from "@/utils/capitalize";

export default {
  components: {
    EgDropdown,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    titleKey: {
      type: String,
      default: "title",
    },
    entity: {
      type: String,
      default: "item",
    },
    filterItemFunction: {
      type: Function,
      default: null,
    },
    groupFunction: {
      type: Function,
      default: null,
    },
    allowSearch: {
      type: Boolean,
      default: true,
    },
    allowDeselect: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dropup: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
    menuClass: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: null,
    },
    hideCreateDivider: {
      type: Boolean,
      default: false,
    },
    allowCustomInput: {
      type: Boolean,
      default: false,
    },
    emptyTitle: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      showSearch: false,
      search: "",
    };
  },

  computed: {
    entityPluralized() {
      switch (this.entity) {
        case "class":
          return "classes";
        case "assignment":
          return "assignments";
        default:
          return this.entity + "s";
      }
    },
    searchBoxValue() {
      if (this.value) {
        return this.getTitle(this.value);
      } else {
        return this.search;
      }
    },
    showCreateEntity() {
      return !!this.$listeners["click:create"];
    },

    itemGroups() {
      return this.groupFunction
        ? this.groupFunction(this.items)
        : [{ id: "all", title: "All", items: this.items }];
    },

    filteredItemGroups() {
      if (!this.search) {
        return this.itemGroups;
      }

      return this.itemGroups
        .map((group) => {
          return {
            ...group,
            items: group.items.filter((item) =>
              this.filterItemFunction
                ? this.filterItemFunction(item)
                : this.getTitle(item)
                    .toLowerCase()
                    .includes(this.search.toLowerCase())
            ),
          };
        })
        .filter((group) => group.items.length > 0);
    },

    filteredItemCount() {
      return this.filteredItemGroups.reduce(
        (acc, curr) => acc + curr.items.length,
        0
      );
    },

    noItemsAvailable() {
      return !this.search && this.items.length === 0;
    },

    hasExactMatch() {
      if (!this.search) return false;
      const trimmed = this.search.trim().toLowerCase();
      return this.items.some(
        (item) => this.getTitle(item)?.toLowerCase() === trimmed
      );
    },

    isValid() {
      if (this.required) {
        // For allowCustomInput, check if object has meaningful content
        if (this.allowCustomInput) {
          return Boolean(
            this.value && (this.value.id || this.value[this.titleKey])
          );
        }
        // For regular usage, use original logic
        return Boolean(this.value);
      }

      return true;
    },
  },

  watch: {
    filteredItemCount(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      this.$nextTick(() => this.$refs.dropdown.update());
    },

    value(newVal) {
      if (!newVal || (!newVal.id && !newVal[this.titleKey])) {
        this.search = "";
      }
    },
  },

  methods: {
    capitalize,
    getTitle(item) {
      if (!this.titleKey.includes("||")) {
        return item[this.titleKey];
      }

      const titleKeysArr = this.titleKey.split("||");

      let title = "";

      for (let i = 0; i < titleKeysArr.length; i++) {
        title = item[titleKeysArr[i].trim()];

        if (title) {
          break;
        }
      }

      return title;
    },
    selectItem(item) {
      this.$emit("input", item);
      this.updateSearch("", false);
    },

    deselect() {
      this.$emit("input", {});
      this.updateSearch("", false);
    },

    clickValue(toggle) {
      if (typeof toggle !== "function") {
        return;
      }
      this.showSearch = true;
      this.$nextTick(() => {
        const input = this.$refs.searchInput;
        const target = input && (input.$el || input);
        toggle({ target: target || this.$el });
      });
    },

    createItem() {
      this.$emit("click:create");
    },

    selectCustomInput() {
      // Just close the dropdown, don't change the current logic
      // The value is already set by updateSearch method
      this.$refs.dropdown.hide();
    },

    updateSearch(value, userTyped = true) {
      if (this.search === value) {
        return;
      }

      this.search = value;
      this.$emit("update:search", value);

      // Emit the text value as input when user types (only if allowCustomInput is enabled)
      if (userTyped && this.allowCustomInput) {
        if (value && value.trim()) {
          const customObject = {
            [this.titleKey]: value.trim(),
          };
          this.$emit("input", customObject);
        } else {
          // When input is cleared, emit empty object to clear the value and fail validation
          this.$emit("input", {});
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-input {
  padding: 0;
  border: none;

  &:focus {
    color: #495057;
    box-shadow: none;
    outline: none;
  }
}

.search-input,
.search-input::placeholder {
  font-family: inherit;
  text-transform: none;
  font-weight: 400;
  color: var(--color-text-neutral-disabled);
  font-size: 1rem;
  line-height: 1.5;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  font-weight: 400;
  color: #212529;
  text-align: left;
  vertical-align: middle;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  background-color: transparent;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.5rem;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  padding-top: 1px;
  padding-bottom: 1px;
  border: 1px solid #ced4da; // TODO: change to CSS variable
}

.selected-item {
  padding: 5px 0;
  width: 90%;
}

.search-select {
  &::v-deep {
    .dropdown-toggle {
      display: none !important;
    }

    .btn {
      padding: 0;
      border: none;
    }

    .dropdown-item-wrapper.sticky {
      background-color: #fff;
    }

    .dropdown-item.active,
    .dropdown-item:active {
      color: #fff;
      text-decoration: none;
      background-color: rgba(111, 66, 193, 1);
    }
  }

  .sticky {
    position: sticky;
  }

  .bottom-0 {
    bottom: 0;
  }

  .bottom-32 {
    bottom: 32px;
  }
}

.create-item::v-deep button {
  color: #714bd4;
}

.create-item::v-deep button:hover {
  color: #532cb5;
  background-color: inherit;
  text-decoration: underline;
}

.no-items {
  padding: 12px;
  color: var(--color-text-neutral-on-white, #1c0f30);
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-300, 14px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-400, 400);
  line-height: 140%;
}

.custom-input-item::v-deep button {
  font-weight: 500;
  border-bottom: 1px solid #e9ecef;
}
</style>
